import {Layout} from 'react-grid-layout';
import {CLChartData} from 'common/src/components/CLChart/CLChart.types';
import {
  AnalyticsEventType,
  AnalyticsFilterItem,
  DateRange,
  DimensionValue,
  InsightsMetricSelectOption,
  MetricAndDimensionOptions,
  MetricsGroupBy,
  MetricValue,
} from '../analytics/analytics.types';
import {CLSelectorOption} from 'common/src/components/Selectors/CLSelector/CLSelector.types';
import {TimezoneObject} from '../timezones/timezones.types';

enum DashboardWidgetSize {
  WIDTH_1_HEIGHT_1 = 'WIDTH_1_HEIGHT_1',
  WIDTH_2_HEIGHT_2 = 'WIDTH_2_HEIGHT_2',
  WIDTH_3_HEIGHT_2 = 'WIDTH_3_HEIGHT_2',
  WIDTH_6_HEIGHT_2 = 'WIDTH_6_HEIGHT_2',

  WIDTH_1_HEIGHT_2 = 'WIDTH_1_HEIGHT_2',
  WIDTH_2_HEIGHT_4 = 'WIDTH_2_HEIGHT_4',
  WIDTH_3_HEIGHT_4 = 'WIDTH_3_HEIGHT_4',
  WIDTH_6_HEIGHT_4 = 'WIDTH_6_HEIGHT_4',
  NONE = '',
}

enum DashboardWidgetType {
  PIE = 'PIE',
  LINE_AREA_CHART = 'LINE_AREA_CHART',
  BAR = 'BAR',
  HORIZONTAL_BAR = 'HORIZONTAL_BAR',
  TABLE = 'TABLE',
  MULTI_TABLE = 'MULTI_TABLE',
  MAP = 'MAP',
  CARD = 'CARD',
  NONE = 'NONE',
}

enum AiDashboardWidgetType {
  PIE = 'Pie (up to 1 metric up to 1 dimension)',
  LINE_AREA_CHART = 'Line (up to 2 metrics up to 1 dimension)',
  BAR = 'Bar stacked (up to 2 metrics up to 1 dimension)',
  HORIZONTAL_BAR = 'Horizontal Bar (up to 2 metrics up to 1 dimension)',
  MULTI_TABLE = 'Multi Table (up to 10 metrics and 3 dimensions)',
}

interface RemoteDashboardWidgetOption {
  maxDims: number;
  maxMetrics: number;
  sizes: RemoteDashboardWidgetSizeOption[];
}

interface RemoteDashboardWidgetSizeOption {
  width: number;
  height: number;
}

interface DashboardWidgetOption {
  maxDimensions: number;
  maxMetrics: number;
  sizes: DashboardWidgetSize[];
}

interface DashboardWidgetConfig {
  colors: string[];
  showTitle: boolean;
  showBackground: boolean;
  hasTransparentBackground: boolean;
  isAvailableExportToPDF: boolean;
  isAvailableExportToXLS: boolean;
  isAvailableToViewInMetricsPage: boolean;
  widgetStyles: React.CSSProperties;
  lowWidgetStyles?: React.CSSProperties;
  isResizable: boolean;
  editSpacing?: string;
}

interface RemoteDashboard extends Dashboard {
  Id: number;
  Name: string;
  IsDefault: boolean;
  ReadOnly: boolean;
}

interface Dashboard {
  id: number;
  name: string;
  isDefault: boolean;
  readOnly: boolean;
}

interface DashboardWidgetBase {
  type: DashboardWidgetType;
  dimensions: DimensionValue[];
  metrics: MetricValue[];
  filters: AnalyticsFilterItem[];
  config: string[];
  breakdownByTime: boolean;
  showTrends: boolean;
  eventType: AnalyticsEventType;
}

interface DashboardWidget extends DashboardWidgetBase {
  id: number;
  name: string;
  dashboardId: number | null;
  layout: Layout;
  children: DashboardWidget[];
}

interface RemoteDashboardWidget {
  breakdownByTime: boolean;
  config: string[];
  dashboardId: number;
  dimensions: string[];
  filters: RemoteFilter[];
  height: number;
  id: number;
  metrics: string[];
  type: string;
  width: number;
  xCoordinate: number;
  yCoordinate: number;
  name: string;
  customConversions: string[];
  showTrends: boolean;
  eventType: AnalyticsEventType;
}

interface RemoteFilter {
  dimension: string;
  filtersValues: string[];
}

interface AddEditWidgetParams extends DashboardWidgetBase {
  id: number;
  name: string;
  width: number;
  height: number;
  size?: DashboardWidgetSize;
}

interface UpdateWidgetProps {
  widget: Partial<DashboardWidget>;
  shouldRefetchData: boolean;
}

interface WidgetUIProps {
  id: string;
  tickAmount?: number;
  chartSize?: number;
  chartData: CLChartData;
  className?: string;
  strokeWidth?: number;
  opacity?: number;
  isSlimChart?: boolean;
  isWidgetWithPoster?: boolean;
  currencySymbol: string;
  isLowChart?: boolean;
  isPreview?: boolean;
  getPosterUrl?: (posterId: string) => string;
  height?: string;
}

interface WidgetByTotalUIProps extends WidgetUIProps {
  isHorizontal: boolean;
  isFilled: boolean;
  distributed?: boolean;
  metrics: MetricValue[];
  dimensions?: DimensionValue[];
  chartType: 'area' | 'bar' | 'line';
}

interface WidgetByDateUIProps extends WidgetByTotalUIProps {
  isStacked: boolean;
  dimensions: DimensionValue[];
  dayLabels: Record<string, string>;
  groupBy: MetricsGroupBy;
  dateRange: DateRange;
  dateFormat: string;
}

interface WidgetDoughnutUIProps extends WidgetUIProps {
  metric: MetricValue;
  isNarrow: boolean;
  isLegendActive: boolean;
  chartType: 'donut';
}

enum DashboardActionOption {
  CreateANewWidget = 'Create Widget',
  CreateANewDashboard = 'Create a New Dashboard',
  SetAsTheMainDashboard = 'Set as Main Dashboard',
  DeleteDashboard = 'Delete Dashboard',
  RenameDashboard = 'Rename Dashboard',
  ExportAsPDF = 'Export to PDF',
  DuplicateDashboard = 'Duplicate Dashboard',
}

enum DashboardModalType {
  None,
  Confirmation,
  AddEditWidget,
  UpdateWidget,
  RenameDashboard,
  DuplicateWidget,
}

interface DashboardPageState {
  activeWidgetId: number | null;
  dashboards: Dashboard[];
  widgets: DashboardWidget[];
  dashboardId: number;
  isLoading: boolean;
  metricsAndDimensionsOptions: MetricAndDimensionOptions;
  filters: InsightsMetricSelectOption<DimensionValue>[];
  startDate: string;
  endDate: string;
  timezone: string;
  timezoneOptions: (CLSelectorOption & TimezoneObject)[];
}

enum DashboardSearchParamKey {
  Date = 'date',
  Timezone = 'timezone',
  Filters = 'filters',
  Widget = 'widget',
  AdservingFilters = 'adservingFilters',
  WebsiteFilters = 'websiteFilters',
}

export {
  RemoteDashboard,
  Dashboard,
  RemoteDashboardWidget,
  DashboardWidget,
  DashboardWidgetType,
  AddEditWidgetParams,
  RemoteFilter,
  WidgetUIProps,
  WidgetByTotalUIProps,
  WidgetByDateUIProps,
  WidgetDoughnutUIProps,
  DashboardWidgetConfig,
  UpdateWidgetProps,
  RemoteDashboardWidgetOption,
  DashboardWidgetOption,
  DashboardWidgetSize,
  DashboardActionOption,
  DashboardModalType,
  AiDashboardWidgetType,
  DashboardPageState,
  DashboardSearchParamKey,
};
